import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { getPendingFeaturesService, markFeatureAsViewedService } from '../services';
import type { Feature } from '../types';

/**
 * Store para gestionar los features pendientes del usuario.
 * Se carga al login y se usa para mostrar novedades/tutoriales.
 */
export const useFeaturesStore = defineStore('features', () => {
  // Estado
  const features = ref<Feature[]>([]);
  const isLoading = ref(false);
  const isLoaded = ref(false);

  // Cola de features a mostrar (ordenados por prioridad)
  const featureQueue = ref<Feature[]>([]);
  const currentFeature = ref<Feature | null>(null);

  // Getters
  const pendingFeatures = computed(() => features.value);

  const pendingFeaturesCount = computed(() => features.value.length);

  const hasPendingFeatures = computed(() => features.value.length > 0);

  /**
   * Obtiene features pendientes para un módulo específico
   */
  const getFeaturesByModule = (module: string) => {
    return computed(() => features.value.filter(f => f.module === null || f.module === module));
  };

  /**
   * Obtiene el siguiente feature en la cola para un módulo
   */
  const getNextFeatureForModule = (module: string): Feature | null => {
    const moduleFeatures = features.value.filter(f => f.module === null || f.module === module);
    return moduleFeatures[0] ?? null;
  };

  // Actions
  /**
   * Carga todos los features pendientes desde el servidor
   * Se llama después del login exitoso
   */
  const fetchPendingFeatures = async (): Promise<void> => {
    if (isLoading.value) return;

    isLoading.value = true;
    try {
      features.value = await getPendingFeaturesService();
      isLoaded.value = true;
    } catch (error) {
      console.error('Error fetching pending features:', error);
      features.value = [];
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Marca un feature como visto y lo remueve de la lista local
   */
  const markAsViewed = async (featureKey: string): Promise<void> => {
    // Optimistic update: remover inmediatamente de la lista local
    features.value = features.value.filter(f => f.feature_key !== featureKey);

    // Si es el feature actual, limpiarlo
    if (currentFeature.value?.feature_key === featureKey) {
      currentFeature.value = null;
    }

    // Sincronizar con el servidor en background
    try {
      await markFeatureAsViewedService(featureKey);
    } catch (error) {
      console.error('Error marking feature as viewed:', error);
      // No revertimos el optimistic update para evitar mostrar el feature de nuevo
    }
  };

  /**
   * Establece el feature actual que se está mostrando
   */
  const setCurrentFeature = (feature: Feature | null): void => {
    currentFeature.value = feature;
  };

  /**
   * Limpia el store (se llama en logout)
   */
  const $reset = (): void => {
    features.value = [];
    featureQueue.value = [];
    currentFeature.value = null;
    isLoading.value = false;
    isLoaded.value = false;
  };

  return {
    // Estado
    features,
    isLoading,
    isLoaded,
    currentFeature,

    // Getters
    pendingFeatures,
    pendingFeaturesCount,
    hasPendingFeatures,
    getFeaturesByModule,
    getNextFeatureForModule,

    // Actions
    fetchPendingFeatures,
    markAsViewed,
    setCurrentFeature,
    $reset,
  };
});
